import React, { useEffect, useState } from 'react'
import { Form, Accordion, Row, Col, InputGroup, Button } from 'react-bootstrap'

export interface EmailConfig {
    header: {
        logoUrl: string
        align: 'left' | 'center' | 'right'
        backgroundColor: string
        url: string
    }
    footer: {
        text: string
        unsubscribeText: string
        socials: { network: string; url: string }[]
    }
    styles: {
        primaryColor: string
        fontFamily: string
    }
}

interface EmailDesignSettingsProps {
    config: EmailConfig
    onChange: (config: EmailConfig) => void
}

export const DEFAULT_EMAIL_CONFIG: EmailConfig = {
    header: {
        logoUrl: '',
        align: 'center',
        backgroundColor: '#ffffff',
        url: '',
    },
    footer: {
        text: '© 2024 My Company',
        unsubscribeText: 'Unsubscribe',
        socials: [],
    },
    styles: {
        primaryColor: '#007bff',
        fontFamily: 'Arial, sans-serif',
    },
}

export const EmailDesignSettings: React.FC<EmailDesignSettingsProps> = ({ config, onChange }) => {
    const handleChange = (section: keyof EmailConfig, key: string, value: any) => {
        onChange({
            ...config,
            [section]: {
                ...config[section],
                [key]: value,
            },
        })
    }

    return (
        <div className='email-design-settings'>
            <Accordion defaultActiveKey='0'>
                <Accordion.Item eventKey='0'>
                    <Accordion.Header>🎨 Header & Styles</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group className='mb-3'>
                            <Form.Label>Logo URL</Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='https://example.com/logo.png'
                                value={config.header.logoUrl}
                                onChange={(e) => handleChange('header', 'logoUrl', e.target.value)}
                            />
                        </Form.Group>

                        <Row className='mb-3'>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Background Color</Form.Label>
                                    <Form.Control
                                        type='color'
                                        value={config.header.backgroundColor}
                                        onChange={(e) => handleChange('header', 'backgroundColor', e.target.value)}
                                        title='Choose your color'
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Alignment</Form.Label>
                                    <div className='btn-group w-100'>
                                        {['left', 'center', 'right'].map((align) => (
                                            <button
                                                key={align}
                                                type='button'
                                                className={`btn btn-outline-secondary ${config.header.align === align ? 'active' : ''}`}
                                                onClick={() => handleChange('header', 'align', align)}
                                            >
                                                <i className={`bi bi-text-${align}`}></i>
                                            </button>
                                        ))}
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className='mb-3'>
                            <Form.Label>Logo Link URL</Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='https://example.com'
                                value={config.header.url}
                                onChange={(e) => handleChange('header', 'url', e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className='mb-3'>
                            <Form.Label>Primary Color (Buttons/Links)</Form.Label>
                            <Form.Control
                                type='color'
                                value={config.styles.primaryColor}
                                onChange={(e) => handleChange('styles', 'primaryColor', e.target.value)}
                            />
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey='1'>
                    <Accordion.Header>👣 Footer</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group className='mb-3'>
                            <Form.Label>Footer Text</Form.Label>
                            <Form.Control
                                as='textarea'
                                rows={2}
                                value={config.footer.text}
                                onChange={(e) => handleChange('footer', 'text', e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className='mb-3'>
                            <Form.Label>Unsubscribe Text</Form.Label>
                            <Form.Control
                                type='text'
                                value={config.footer.unsubscribeText}
                                onChange={(e) => handleChange('footer', 'unsubscribeText', e.target.value)}
                            />
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    )
}
